// @flow
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useIntl } from 'react-intl';
import type { CablePath } from 'esm-web-pro/types/networks';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { useLeaflet } from 'react-leaflet';
import { numberPointCallback } from 'esm-web-pro/containers/pages/network/objects/cable/MapCable';
import type { LoadingCallback } from 'esm-web-pro/containers/map/editor/BaseEditor';


type Props = {
    cablePaths: CablePath[],
    clicked: string,
    setClicked: string,
    highlightCable: Function,
};

export const numberPointsCallbacksCablePath = (cablePathPoints: Object): LoadingCallback[] => {
    const {
        start_termination_point: startTerminationPoint, end_termination_point: endTerminationPoint,
    } = cablePathPoints;

    if (!startTerminationPoint && !endTerminationPoint) return [];
    return [numberPointCallback(startTerminationPoint), numberPointCallback(endTerminationPoint)];
};

const CablePaths = (props: Props) => {
    const { cablePaths, clicked, setClicked } = props;
    const { formatMessage } = useIntl();

    const editor = React.useContext(EditorContext);
    const { map } = useLeaflet();

    const showPathOnMap = (cablePath: CablePath) => {
        const { sequence_number: sequence } = cablePath;

        const coords = cablePath.path.coordinates.map((c) => [c[1], c[0]]);
        const highlightCable = UseHighlightCable(coords);
        editor.unregisterAllLoadingCallback();
        editor.registerMultipleLoadingCallbacks(numberPointsCallbacksCablePath(cablePath.container_points));
        editor.registerLoadingCallback(highlightCable);
        editor.reloadFeatures();
        if (map) {
            // Go to the middle path of the cable
            map.highlightedGeometry = {
                bounds: coords, center: null,
            };
            map.panInsideBounds(coords);
        }
        const rowId = `path-${sequence}`;
        setClicked(rowId);
    };

    if (isEmpty(cablePaths)) {
        return null;
    }

    return cablePaths.map((cablePath: CablePath) => {
        const {
            sequence_number: pathSequence,
            provider_ref: providerRef,
        } = cablePath;

        let title = `${formatMessage(cableAndFiberMessages.cablePath)} ${pathSequence}`;
        if (providerRef) {
            title += ` ${providerRef}`;
        }

        return (
            <ContentRow
                key={`path-${pathSequence}`}
                rowId={`path-${pathSequence}`}
                padLevel={1}
                clickedRowIdentity={clicked}
                onClick={() => showPathOnMap(cablePath)}
            >
                <ValueText>
                    {title}
                </ValueText>
            </ContentRow>
        );
    });
};

export default CablePaths;
